<template>
  <!-- System message -->
  <div v-if="isSystemMessage" class="system-message">
    <div class="system-message-content">
      {{ message.content }}
    </div>
  </div>

  <!-- Regular user message -->
  <div
    v-else
    :class="[
      'message-item',
      isOwnMessage ? 'own-message' : 'other-message',
      message.edited_at ? 'edited' : ''
    ]"
  >
    <div class="message-header" v-if="!isOwnMessage">
      <span class="sender-name">
        {{ senderName }}
      </span>
    </div>
    <div class="message-bubble">
      <div
        v-if="message.content"
        class="message-content"
        v-html="formattedContent"
      ></div>
      <div v-if="message.attachments && message.attachments.length" class="attachments">
        <div
          v-for="att in message.attachments"
          :key="att.id"
          class="attachment-chip"
          @click="openPreview(att)"
          :title="att.original_filename"
        >
          <i :class="['fa-solid', getFileIcon(att.mimetype, att.original_filename), 'attachment-icon', getFileIconClass(att.mimetype)]"></i>
          <span class="attachment-name">{{ att.original_filename }}</span>
        </div>
      </div>

      <!-- Preview Modal -->
      <AttachmentPreviewModal
        v-if="selectedAttachment"
        v-model="previewOpen"
        :attachment="selectedAttachment"
      />
      <div class="message-meta">
        <span class="message-time">{{ formattedTime }}</span>
        <span v-if="message.edited_at" class="edited-indicator">(edited)</span>
        <button
          v-if="canEdit"
          class="edit-button"
          @click="startEditing"
          title="Edit message"
        >
          <i class="fa-solid fa-pencil"></i>
        </button>
      </div>
    </div>

    <!-- Edit Mode -->
    <div v-if="isEditing" class="edit-overlay">
      <div class="edit-container">
        <textarea
          v-model="editContent"
          class="edit-textarea"
          rows="3"
          @keydown.enter.exact.prevent="saveEdit"
          @keydown.esc="cancelEdit"
        ></textarea>
        <div class="edit-actions">
          <button class="btn btn-sm btn-secondary" @click="cancelEdit">
            Cancel
          </button>
          <button
            class="btn btn-sm btn-primary"
            @click="saveEdit"
            :disabled="!editContent.trim() || editContent === message.content"
          >
            Save
          </button>
        </div>
        <div class="edit-hint">
          Press Enter to save, Esc to cancel
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { linkify } from "@/utils/linkify";
import { useLoginStore } from "@/stores/loginStore";
import AttachmentPreviewModal from "./AttachmentPreviewModal.vue";

export default {
  name: "MessageItem",
  components: { AttachmentPreviewModal },
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loginStore: useLoginStore(),
      isEditing: false,
      editContent: "",
      selectedAttachment: null,
      previewOpen: false,
    };
  },
  computed: {
    isSystemMessage() {
      return this.message.message_type === "system";
    },

    isOwnMessage() {
      return this.message.id_users === this.loginStore.user?.id;
    },

    senderName() {
      if (!this.message.sender) return "Unknown";
      return `${this.message.sender.first_name} ${this.message.sender.last_name}`;
    },

    formattedContent() {
      return linkify(this.message.content);
    },

    formattedTime() {
      if (!this.message.createdAt) return "";
      const date = new Date(this.message.createdAt);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();

      if (isToday) {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();

      if (isYesterday) {
        return `Yesterday ${date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })}`;
      }

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    canEdit() {
      if (!this.isOwnMessage) return false;
      const createdAt = new Date(this.message.createdAt);
      const now = new Date();
      const diffMinutes = (now - createdAt) / (1000 * 60);
      return diffMinutes <= 5;
    },
  },
  methods: {
    getFileIcon(mimetype, filename) {
      if (mimetype?.startsWith("image/")) return "fa-file-image";
      if (mimetype === "application/pdf") return "fa-file-pdf";
      if (mimetype?.includes("word") || mimetype?.includes("msword")) return "fa-file-word";
      if (mimetype?.includes("excel") || mimetype?.includes("spreadsheet")) return "fa-file-excel";
      if (mimetype?.includes("powerpoint") || mimetype?.includes("presentation")) return "fa-file-powerpoint";
      if (mimetype?.startsWith("video/")) return "fa-file-video";
      if (mimetype?.startsWith("audio/")) return "fa-file-audio";
      if (mimetype === "text/plain") return "fa-file-alt";
      if (mimetype === "text/csv") return "fa-file-csv";
      if (mimetype?.includes("zip") || mimetype?.includes("archive") || mimetype?.includes("compressed")) return "fa-file-archive";
      const ext = filename?.split(".").pop()?.toLowerCase();
      const map = {
        doc: "fa-file-word", docx: "fa-file-word",
        xls: "fa-file-excel", xlsx: "fa-file-excel",
        ppt: "fa-file-powerpoint", pptx: "fa-file-powerpoint",
        zip: "fa-file-archive", rar: "fa-file-archive",
        txt: "fa-file-alt", csv: "fa-file-csv",
      };
      return map[ext] || "fa-file";
    },

    getFileIconClass(mimetype) {
      if (mimetype?.startsWith("image/")) return "icon-image";
      if (mimetype === "application/pdf") return "icon-pdf";
      if (mimetype?.includes("word") || mimetype?.includes("msword")) return "icon-word";
      if (mimetype?.includes("excel") || mimetype?.includes("spreadsheet")) return "icon-excel";
      if (mimetype?.includes("powerpoint") || mimetype?.includes("presentation")) return "icon-ppt";
      if (mimetype?.startsWith("video/")) return "icon-video";
      if (mimetype?.startsWith("audio/")) return "icon-audio";
      return "icon-generic";
    },

    openPreview(att) {
      this.selectedAttachment = att;
      this.previewOpen = true;
    },

    startEditing() {
      this.editContent = this.message.content;
      this.isEditing = true;
    },

    cancelEdit() {
      this.isEditing = false;
      this.editContent = "";
    },

    saveEdit() {
      if (!this.editContent.trim() || this.editContent === this.message.content) {
        return;
      }
      this.$emit("edit", {
        messageId: this.message.id,
        content: this.editContent.trim(),
      });
      this.isEditing = false;
    },
  },
};
</script>

<style scoped>
.system-message {
  align-self: center;
  width: 100%;
  margin: 16px 0;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.system-message-content {
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.message-item {
  display: flex;
  flex-direction: column;
  max-width: 75%;
  margin-bottom: 12px;
  position: relative;
}

.own-message {
  align-self: flex-end;
  margin-left: auto;
}

.other-message {
  align-self: flex-start;
  margin-right: auto;
}

.message-header {
  margin-bottom: 4px;
  padding-left: 12px;
}

.sender-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.message-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  word-wrap: break-word;
  position: relative;
}

.own-message .message-bubble {
  background: linear-gradient(135deg, #6BA6CD, #4a8ab8);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.other-message .message-bubble {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border-bottom-left-radius: 4px;
}

.message-content {
  line-height: 1.4;
  white-space: pre-wrap;
}

.message-content :deep(.message-link) {
  color: inherit;
  text-decoration: underline;
  opacity: 0.9;
}

.message-content :deep(.message-link):hover {
  opacity: 1;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.7;
}

.edited-indicator {
  font-style: italic;
}

.edit-button {
  background: none;
  border: none;
  padding: 2px 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  color: inherit;
  font-size: 11px;
}

.message-item:hover .edit-button {
  opacity: 0.6;
}

.edit-button:hover {
  opacity: 1 !important;
}

.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-container {
  background: #2a2a2a;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.edit-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 14px;
  resize: vertical;
  margin-bottom: 12px;
}

.edit-textarea:focus {
  outline: none;
  border-color: #6BA6CD;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.edit-hint {
  margin-top: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
}

.attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.attachment-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  max-width: 200px;
  overflow: hidden;
}

.attachment-chip:hover {
  background: rgba(255, 255, 255, 0.2);
}

.attachment-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.attachment-name {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-pdf    { color: #e74c3c; }
.icon-word   { color: #2b579a; }
.icon-excel  { color: #217346; }
.icon-ppt    { color: #d24726; }
.icon-image  { color: #9b59b6; }
.icon-video  { color: #e67e22; }
.icon-audio  { color: #1abc9c; }
.icon-generic { color: rgba(255, 255, 255, 0.7); }
</style>
